import { Action, Actions, State, action, Thunk, thunk } from 'easy-peasy'

import incense from '@mortgage-pos/ui/incense'
import {
  OwnershipClassification,
  PlaidAccount,
  QuestionType,
} from '@mortgage-pos/types'
import { simpleV2TrackingService } from '@mortgage-pos/shared-frontend'
import { borrowerPortalRequest } from '../services/http'
import type {
  AIAddressResult,
  IncomeOwnership,
  PlaidIncomeAnalysisStatusResult,
  PlaidIncomeAttributionResult,
} from '@mortgage-pos/services/plaid'
import type { StoreModel } from './index'

const ADDRESS_CONFIDENCE_THRESHOLD = 0.4
const PLAID_LINK_TOKEN_FAILED_ERROR_DETAILS = 'Plaid link token request failed'
const PLAID_ANALYSIS_FAILED_ERROR_DETAILS =
  'Plaid income analysis request failed'
const FANNIE_ANALYSIS_POLL_INTERVAL_MS = 5000
const FANNIE_ANALYSIS_TIMEOUT_MS = 10 * 60 * 1000

export type IncomeAttributionSaveResult = 'saved' | 'stale' | 'failed'

interface PlaidState {
  accounts: PlaidAccount[] | null
  linkToken: string | null
  isLoading: boolean
  error: string | null
  hasBlockingPlaidError: boolean
  isPlaidLinkOpen: boolean
  usePlaidUpload: boolean | undefined
  isAnalyzingIncome: boolean
  incomeAnalysis: PlaidIncomeAnalysisStatusResult | null
  incomeAnalysisError: string | null
  assets: PlaidAccount[] | null
  totalCurrentBalance: number
  isLoadingAssets: boolean
  incomeAttribution: PlaidIncomeAttributionResult | null
  incomeOwnerships: Array<IncomeOwnership | null>
  isLoadingIncomeAttribution: boolean
  incomeAttributionError: string | null
  getConnectedAccounts: Thunk<PlaidState>
  setLinkToken: Action<PlaidState, string>
  setLoading: Action<PlaidState, boolean>
  setError: Action<PlaidState, string | null>
  setHasBlockingPlaidError: Action<PlaidState, boolean>
  setIsPlaidLinkOpen: Action<PlaidState, boolean>
  setAccounts: Action<PlaidState, PlaidAccount[]>
  setUsePlaidUpload: Action<PlaidState, boolean | undefined>
  setIsAnalyzingIncome: Action<PlaidState, boolean>
  setIncomeAnalysis: Action<PlaidState, PlaidIncomeAnalysisStatusResult | null>
  setIncomeAnalysisError: Action<PlaidState, string | null>
  setAssets: Action<
    PlaidState,
    { totalCurrentBalance: number; accounts: PlaidAccount[] }
  >
  setIsLoadingAssets: Action<PlaidState, boolean>
  setIncomeAttribution: Action<PlaidState, PlaidIncomeAttributionResult | null>
  setIncomeOwnership: Action<
    PlaidState,
    { incomeIndex: number; ownership: IncomeOwnership }
  >
  setIsLoadingIncomeAttribution: Action<PlaidState, boolean>
  setIncomeAttributionError: Action<PlaidState, string | null>
  initializePlaid: Thunk<PlaidState, void, object, StoreModel>
  trackSimpleV2PlaidLinkReady: Thunk<PlaidState, void, object, StoreModel>
  trackSimpleV2PlaidConnected: Thunk<PlaidState, void, object, StoreModel>
  analyzeIncome: Thunk<PlaidState, void, object, StoreModel>
  fetchIncomeAnalysis: Thunk<PlaidState, void, object, StoreModel>
  fetchAssets: Thunk<PlaidState>
  fetchIncomeAttribution: Thunk<
    PlaidState,
    void,
    object,
    StoreModel,
    Promise<PlaidIncomeAttributionResult | null>
  >
  saveIncomeAttribution: Thunk<
    PlaidState,
    IncomeOwnership[] | void,
    object,
    StoreModel,
    Promise<IncomeAttributionSaveResult>
  >
  assignAllIncomeToPrimary: Thunk<
    PlaidState,
    void,
    object,
    StoreModel,
    Promise<IncomeAttributionSaveResult>
  >
}

const plaidStore: PlaidState = {
  accounts: null,
  linkToken: null,
  isLoading: false,
  error: null,
  hasBlockingPlaidError: false,
  isPlaidLinkOpen: false,
  usePlaidUpload: undefined,
  isAnalyzingIncome: false,
  incomeAnalysis: null,
  incomeAnalysisError: null,
  assets: null,
  totalCurrentBalance: 0,
  isLoadingAssets: false,
  incomeAttribution: null,
  incomeOwnerships: [],
  isLoadingIncomeAttribution: false,
  incomeAttributionError: null,

  // Actions
  setLinkToken: action((state, payload) => {
    state.linkToken = payload
  }),

  setLoading: action((state, payload) => {
    state.isLoading = payload
  }),

  setError: action((state, payload) => {
    state.error = payload
  }),

  setHasBlockingPlaidError: action((state, payload) => {
    state.hasBlockingPlaidError = payload
  }),

  setIsPlaidLinkOpen: action((state, payload) => {
    state.isPlaidLinkOpen = payload
  }),

  setAccounts: action((state, payload) => {
    state.accounts = payload
  }),

  setUsePlaidUpload: action((state, payload) => {
    state.usePlaidUpload = payload
  }),

  setIsAnalyzingIncome: action((state, payload) => {
    state.isAnalyzingIncome = payload
  }),

  setIncomeAnalysis: action((state, payload) => {
    state.incomeAnalysis = payload
  }),

  setIncomeAnalysisError: action((state, payload) => {
    state.incomeAnalysisError = payload
  }),

  setAssets: action((state, payload) => {
    state.assets = payload.accounts
    state.totalCurrentBalance = payload.totalCurrentBalance
  }),

  setIsLoadingAssets: action((state, payload) => {
    state.isLoadingAssets = payload
  }),

  setIncomeAttribution: action((state, payload) => {
    state.incomeAttribution = payload
    state.incomeOwnerships = (payload?.incomes ?? []).map(
      (income) => income.suggestedOwnership
    )
  }),

  setIncomeOwnership: action((state, { incomeIndex, ownership }) => {
    state.incomeOwnerships[incomeIndex] = ownership
  }),

  setIsLoadingIncomeAttribution: action((state, payload) => {
    state.isLoadingIncomeAttribution = payload
  }),

  setIncomeAttributionError: action((state, payload) => {
    state.incomeAttributionError = payload
  }),

  // Thunks
  initializePlaid: thunk(
    async (actions, _, { getState, getStoreState, getStoreActions }) => {
      const { isLoading } = getState()
      const { questionnaire } = getStoreState()
      const {
        pageIndex,
        isSimpleV2Flow,
        isSimpleV2FannieFlow,
        currentPageName,
      } = questionnaire
      const { tracking } = getStoreActions()

      if (isLoading) {
        return
      }

      if (isSimpleV2Flow) {
        actions.setHasBlockingPlaidError(false)
        actions.setIsPlaidLinkOpen(false)
      }
      actions.setError(null)
      actions.setLoading(true)
      try {
        if (isSimpleV2FannieFlow) {
          getStoreActions().fannie.setConnectToken(null)
          getStoreActions().fannie.setConnectUrlUsed(true)
          await getStoreActions().fannie.getConnectUrl(null)
          const connectToken = getStoreState().fannie.connectToken
          if (!connectToken) {
            throw new Error('Fannie Mae did not return a Plaid Link token')
          }
        } else {
          const response = await borrowerPortalRequest(
            '/plaid/link-token',
            isSimpleV2Flow ? { useExtendedAssetReportHistory: true } : {}
          )
          actions.setLinkToken(response.data.linkToken)
        }
        if (isSimpleV2Flow) {
          simpleV2TrackingService.simpleV2PlaidLinkTokenCreated(
            currentPageName,
            pageIndex
          )
          tracking.elementLoaded({
            elementName: 'plaid_link_token_created',
            location: 'simple_v2_lifecycle',
          })
        }
      } catch (error) {
        const errorMessage = 'Failed to initialize Plaid'
        actions.setError(errorMessage)
        if (isSimpleV2Flow) {
          actions.setHasBlockingPlaidError(true)
          simpleV2TrackingService.simpleV2PlaidError(currentPageName, pageIndex)
          tracking.formErrored({
            errorType: 'plaid_link_token_failed',
            errorMessage,
            errorDetails: PLAID_LINK_TOKEN_FAILED_ERROR_DETAILS,
          })
        }
      } finally {
        actions.setLoading(false)
      }
    }
  ),

  trackSimpleV2PlaidLinkReady: thunk(
    (_, __, { getStoreState, getStoreActions }) => {
      const { questionnaire } = getStoreState()
      const { isSimpleV2Flow } = questionnaire
      const { tracking } = getStoreActions()

      if (!isSimpleV2Flow) {
        return
      }

      tracking.elementLoaded({
        elementName: 'plaid_link_ready',
        location: 'simple_v2_lifecycle',
      })
    }
  ),

  trackSimpleV2PlaidConnected: thunk(
    (actions, __, { getStoreState, getStoreActions }) => {
      const { questionnaire } = getStoreState()
      const { isSimpleV2Flow, currentPageName, pageIndex } = questionnaire
      const { tracking } = getStoreActions()

      if (!isSimpleV2Flow) {
        return
      }

      actions.setHasBlockingPlaidError(false)
      simpleV2TrackingService.simpleV2PlaidConnected(currentPageName, pageIndex)
      tracking.elementLoaded({
        elementName: 'plaid_connection_completed',
        location: 'simple_v2_lifecycle',
      })
    }
  ),

  getConnectedAccounts: thunk(
    async (actions, _, { getState, getStoreState }) => {
      if (getState().isLoading) {
        return
      }

      actions.setLoading(true)

      try {
        const accounts = await getPlaidAccounts(
          getState().linkToken,
          getStoreState().questionnaire.isSimpleV2FannieFlow
        )
        actions.setAccounts(accounts)
      } catch (error) {
        incense(error)
          .details({
            name: 'PlaidGetConnectedAccountsError',
            message: 'Failed to get connected accounts',
          })
          .error()
      } finally {
        actions.setLoading(false)
      }
    }
  ),

  /**
   * Triggers AI income analysis from the Plaid asset report.
   * This is a long-running request (1-5+ minutes) that polls for the
   * consolidated report token, fetches the report, and runs AI analysis.
   */
  analyzeIncome: thunk(
    async (actions, _, { getState, getStoreActions, getStoreState }) => {
      const getQuestionnaireState = () => getStoreState().questionnaire
      const getAnswersState = () => getStoreState().answers
      const {
        tracking,
        answers: answersActions,
        application: applicationActions,
      } = getStoreActions()
      const { isAnalyzingIncome, incomeAnalysis } = getState()
      const { applicationGuid } = getStoreState().application
      const { slug, isSimpleV2FannieFlow } = getQuestionnaireState()
      const isStaleFannieRequest = () =>
        isSimpleV2FannieFlow &&
        (getStoreState().application.applicationGuid !== applicationGuid ||
          getQuestionnaireState().slug !== slug)

      if (isAnalyzingIncome || incomeAnalysis) {
        return
      }

      actions.setIsAnalyzingIncome(true)
      actions.setIncomeAnalysisError(null)

      try {
        let result: PlaidIncomeAnalysisStatusResult

        if (isSimpleV2FannieFlow) {
          const startedAt = Date.now()
          while (!isStaleFannieRequest()) {
            const response = await borrowerPortalRequest(
              '/fannie/income-analysis',
              {},
              {},
              'get'
            ).catch(() => null)

            if (isStaleFannieRequest()) return

            result = response?.data as PlaidIncomeAnalysisStatusResult
            if (result?.status === 'completed') {
              await actions.getConnectedAccounts()
              if (getState().accounts !== null) break
            }
            if (result?.status === 'failed') {
              throw new Error('Income analysis failed')
            }
            if (Date.now() - startedAt >= FANNIE_ANALYSIS_TIMEOUT_MS) {
              throw new Error('Income analysis failed')
            }

            await new Promise((resolve) =>
              setTimeout(resolve, FANNIE_ANALYSIS_POLL_INTERVAL_MS)
            )
          }
        } else {
          const response = await borrowerPortalRequest(
            '/plaid/income-analysis',
            {
              deferIncomeUntilAttributed: hasCoBorrowerQuestion(
                getQuestionnaireState().pages
              ),
            },
            {},
            'post'
          )
          result = response.data as PlaidIncomeAnalysisStatusResult
        }

        if (isStaleFannieRequest()) return

        setSecondAccountOwnerAnswer(result, answersActions)
        actions.setIncomeAnalysis(result)
        const { isSimpleV2Flow } = getQuestionnaireState()
        if (isSimpleV2Flow && hasUsableCompletedIncomeAnalysis(result)) {
          tracking.elementLoaded({
            elementName: 'plaid_analysis_completed',
            location: 'simple_v2_lifecycle',
          })
        }
        await commitCurrentAddressFromAnalysis(
          result,
          getAnswersState,
          answersActions,
          applicationActions
        )
      } catch (error: any) {
        if (isStaleFannieRequest()) return

        const message =
          error?.response?.data?.error ||
          error?.message ||
          'Income analysis failed'
        actions.setIncomeAnalysisError(message)
        if (getQuestionnaireState().isSimpleV2Flow) {
          tracking.formErrored({
            errorType: 'plaid_analysis_failed',
            errorMessage: 'Income analysis failed',
            errorDetails: PLAID_ANALYSIS_FAILED_ERROR_DETAILS,
          })
        }

        incense(error)
          .details({
            name: 'PlaidAnalyzeIncomeError',
            message: 'Failed to analyze income from asset report',
          })
          .error()
      } finally {
        actions.setIsAnalyzingIncome(false)
      }
    }
  ),

  fetchIncomeAttribution: thunk(async (actions) => {
    actions.setIsLoadingIncomeAttribution(true)
    actions.setIncomeAttributionError(null)

    try {
      const response = await borrowerPortalRequest(
        '/plaid/income-attribution',
        {},
        {},
        'get'
      )
      const attribution = response.data as PlaidIncomeAttributionResult
      actions.setIncomeAttribution(attribution)
      return attribution
    } catch (error) {
      actions.setIncomeAttributionError('Failed to load income')
      incense(error)
        .details({
          name: 'PlaidFetchIncomeAttributionError',
          message: 'Failed to fetch income attribution',
        })
        .error()
      return null
    } finally {
      actions.setIsLoadingIncomeAttribution(false)
    }
  }),

  saveIncomeAttribution: thunk(async (actions, ownerships, { getState }) => {
    const { incomeAttribution, incomeOwnerships } = getState()

    if (!incomeAttribution?.analysisId) {
      return 'saved'
    }

    actions.setIncomeAttributionError(null)

    try {
      await borrowerPortalRequest(
        '/plaid/income-attribution',
        {
          analysisId: incomeAttribution.analysisId,
          ownerships: ownerships || incomeOwnerships,
        },
        {},
        'post'
      )
      return 'saved'
    } catch (error: any) {
      if (error?.response?.status === 409) {
        return 'stale'
      }

      actions.setIncomeAttributionError('Failed to save income')
      incense(error)
        .details({
          name: 'PlaidSaveIncomeAttributionError',
          message: 'Failed to save income attribution',
        })
        .error()
      return 'failed'
    }
  }),

  assignAllIncomeToPrimary: thunk(async (actions) => {
    const attribution = await actions.fetchIncomeAttribution()

    if (!attribution) {
      return 'failed'
    }

    return actions.saveIncomeAttribution(
      attribution.incomes.map(() => OwnershipClassification.Primary)
    )
  }),

  fetchAssets: thunk(async (actions, _, { getState, getStoreState }) => {
    const { isLoadingAssets } = getState()

    if (isLoadingAssets) {
      return
    }

    actions.setIsLoadingAssets(true)

    try {
      const response = await borrowerPortalRequest(
        getStoreState().questionnaire.isSimpleV2FannieFlow
          ? '/fannie/assets'
          : '/plaid/assets',
        {},
        {},
        'get'
      )
      actions.setAssets(response.data)
    } catch (error) {
      incense(error)
        .details({
          name: 'PlaidFetchAssetsError',
          message: 'Failed to fetch Plaid assets',
        })
        .error()
    } finally {
      actions.setIsLoadingAssets(false)
    }
  }),

  /**
   * Recovery: fetches the latest income analysis result for the current application.
   * Used when the user returns after a connection drop.
   */
  fetchIncomeAnalysis: thunk(
    async (actions, _, { getStoreActions, getStoreState }) => {
      const getAnswersState = () => getStoreState().answers
      const { answers: answersActions, application: applicationActions } =
        getStoreActions()

      try {
        const response = await borrowerPortalRequest(
          getStoreState().questionnaire.isSimpleV2FannieFlow
            ? '/fannie/income-analysis'
            : '/plaid/income-analysis',
          {},
          {},
          'get'
        )

        const result = response.data as PlaidIncomeAnalysisStatusResult

        if (result.status === 'completed') {
          setSecondAccountOwnerAnswer(result, answersActions)
          actions.setIncomeAnalysis(result)
          await commitCurrentAddressFromAnalysis(
            result,
            getAnswersState,
            answersActions,
            applicationActions
          )
        } else if (result.status === 'failed') {
          actions.setIncomeAnalysisError(result.error || 'Analysis failed')
        }
      } catch (error) {
        incense(error)
          .details({
            name: 'PlaidFetchIncomeAnalysisError',
            message: 'Failed to fetch income analysis status',
          })
          .error()
      }
    }
  ),
}

async function getPlaidAccounts(linkToken?: string, isFannie = false) {
  const { data } = await borrowerPortalRequest(
    isFannie
      ? '/fannie/assets'
      : linkToken
        ? `/plaid/accounts?linkToken=${linkToken}`
        : '/plaid/accounts',
    {},
    {},
    'get'
  )

  return data?.accounts ?? []
}

function hasCoBorrowerQuestion(
  pages: State<StoreModel>['questionnaire']['pages']
): boolean {
  return (pages ?? []).some((page) =>
    (page?.questions ?? []).some(
      (question) =>
        question?.component === QuestionType.SimpleV2CoBorrowerQuestion
    )
  )
}

function setSecondAccountOwnerAnswer(
  result: PlaidIncomeAnalysisStatusResult,
  answersActions: Actions<StoreModel>['answers']
) {
  const detected = result.secondAccountOwnerDetected
  const ownerNames = result.secondAccountOwnerNames

  answersActions.setAnswers({
    secondAccountOwnerDetected:
      typeof detected === 'boolean' ? (detected ? 'Yes' : 'No') : undefined,
    secondAccountOwnerNames: ownerNames?.length ? ownerNames : undefined,
  })
}

function hasUsableCompletedIncomeAnalysis(
  result: PlaidIncomeAnalysisStatusResult
) {
  const grossAnnual = result.data?.gross_income_estimate?.estimated_gross_annual

  return (
    result.status === 'completed' &&
    !result.error &&
    typeof grossAnnual === 'number' &&
    grossAnnual > 0
  )
}

/**
 * Picks the best current-residence address from the AI analysis result.
 * Prefers the highest-confidence address flagged as `is_current`, and
 * falls back to the highest-confidence address above the threshold.
 * Mirrors the logic in `buildPrefillFromAIResult` on the API side so the
 * UI stays resilient if the backend prefill shape changes.
 */
function pickCurrentAddress(
  addresses?: AIAddressResult[]
): AIAddressResult | null {
  if (!addresses?.length) return null

  const confident = addresses.filter(
    (addr) => addr.confidence >= ADDRESS_CONFIDENCE_THRESHOLD
  )

  if (!confident.length) return null

  const byConfidence = [...confident].sort(
    (a, b) => b.confidence - a.confidence
  )

  return byConfidence.find((addr) => addr.is_current) ?? byConfidence[0]
}

/**
 * Commits the AI-detected current residence into the questionnaire answer
 * system using the same `addresses+0` / `addresses` shape produced by the
 * address autocomplete flow, then dispatches `application.save` so the
 * address is persisted via `ApplicationClient.fillAddress` — the same DB
 * write path used when a user enters the address manually.
 */
async function commitCurrentAddressFromAnalysis(
  result: PlaidIncomeAnalysisStatusResult,
  getAnswersState: () => State<StoreModel>['answers'],
  answersActions: Actions<StoreModel>['answers'],
  applicationActions: Actions<StoreModel>['application']
) {
  const best = pickCurrentAddress(result.data?.addresses)
  if (!best) return

  const existing = getAnswersState().mergedAnswers['addresses+0'] as
    | Array<{ type?: string; street?: string }>
    | undefined
  const hasCurrent = existing?.some(
    (a) => a?.type === 'current' && Boolean(a?.street)
  )
  if (hasCurrent) return

  const addressAnswer = {
    id: null,
    type: 'current',
    street: best.street || '',
    apt: '',
    city: best.city || '',
    state: (best as { state?: string }).state || best.region || '',
    zipCode: best.postal_code || '',
  }

  const payload = {
    'addresses+0': [addressAnswer],
    addresses: [addressAnswer],
  }

  answersActions.setAnswers(payload)

  try {
    await applicationActions.save(payload)
  } catch (error) {
    incense(error)
      .details({
        name: 'PlaidCommitCurrentAddressError',
        message:
          'Failed to persist AI-detected current address via application.save',
      })
      .error()
  }
}

export default plaidStore
